import React, { useState } from 'react';
import { AgentTemplate, CloneTemplateRequest } from '../../../services/api';
import styles from './TemplateDetailModal.module.css';

interface TemplateDetailModalProps {
  template: AgentTemplate;
  isOpen?: boolean;
  onClose: () => void;
  onClone: (template: AgentTemplate, customization: CloneTemplateRequest) => void;
  cloning?: boolean;
}

export const TemplateDetailModal: React.FC<TemplateDetailModalProps> = ({
  template,
  isOpen = true,
  onClose,
  onClone,
  cloning = false,
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'capabilities' | 'customize'>('overview');
  const [customization, setCustomization] = useState<CloneTemplateRequest>({
    customName: `My ${template.name}`,
    customDescription: template.description,
    customSystemInstructions: template.systemInstructions,
  });

  if (!isOpen) return null;

  const handleClone = () => {
    onClone(template, customization);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const renderRating = (rating: number) => {
    const fullStars = Math.floor(rating);
    const stars = [];
    for (let i = 0; i < fullStars; i++) {
      stars.push(<span key={i} className={styles.star}>★</span>);
    }
    for (let i = fullStars; i < 5; i++) {
      stars.push(<span key={i} className={styles.starEmpty}>☆</span>);
    }
    return stars;
  };

  return (
    <div className={styles.overlay} onClick={handleBackdropClick}>
      <div className={styles.modal}>
        <button className={styles.closeButton} onClick={onClose} aria-label="Close">
          ×
        </button>

        <div className={styles.header}>
          <div className={styles.headerIcon}>{template.icon}</div>
          <div className={styles.headerContent}>
            <div className={styles.titleRow}>
              <h2 className={styles.title}>{template.name}</h2>
              {(template.verified || template.isVerified) && (
                <span className={styles.verifiedBadge}>✓ Verified</span>
              )}
            </div>
            <p className={styles.subtitle}>{template.shortDescription || template.description?.substring(0, 100)}</p>
            <div className={styles.meta}>
              <span className={styles.rating}>
                {renderRating(template.rating ?? template.averageRating ?? 0)}
                <span className={styles.ratingValue}>{(template.rating ?? template.averageRating ?? 0).toFixed(1)}</span>
                <span className={styles.reviewCount}>({template.reviewCount ?? 0} reviews)</span>
              </span>
              <span className={styles.divider}>•</span>
              <span className={styles.clones}>{(template.cloneCount ?? template.totalClones ?? 0).toLocaleString()} clones</span>
              <span className={styles.divider}>•</span>
              <span className={styles.category}>{template.category}</span>
            </div>
          </div>
        </div>

        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'overview' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'capabilities' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('capabilities')}
          >
            Capabilities ({template.capabilities.length})
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'customize' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('customize')}
          >
            Customize & Clone
          </button>
        </div>

        <div className={styles.content}>
          {activeTab === 'overview' && (
            <div className={styles.overview}>
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Description</h3>
                <p className={styles.description}>{template.description}</p>
              </section>

              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Tags</h3>
                <div className={styles.tags}>
                  {template.tags.map((tag) => (
                    <span key={tag} className={styles.tag}>{tag}</span>
                  ))}
                </div>
              </section>

              {template.integrations && template.integrations.length > 0 && (
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>Integrations</h3>
                  <div className={styles.integrationsList}>
                    {template.integrations.map((integration) => (
                      <div key={integration.id} className={styles.integrationItem}>
                        <span className={styles.integrationIcon}>{integration.icon}</span>
                        <div className={styles.integrationInfo}>
                          <span className={styles.integrationName}>{integration.name}</span>
                          <span className={styles.integrationDesc}>{integration.description}</span>
                          <span className={`${styles.integrationRequired} ${integration.required ? styles.required : styles.optional}`}>
                            {integration.required ? 'Required' : 'Optional'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Conversation Starters</h3>
                <div className={styles.starters}>
                  {template.conversationStarters.map((starter, index) => (
                    <div key={index} className={styles.starter}>
                      "{starter}"
                    </div>
                  ))}
                </div>
              </section>
            </div>
          )}

          {activeTab === 'capabilities' && (
            <div className={styles.capabilitiesTab}>
              {template.capabilities.map((capability) => (
                <div key={capability.id} className={styles.capabilityCard}>
                  <div className={styles.capabilityHeader}>
                    <span className={styles.capabilityIcon}>{capability.icon}</span>
                    <h4 className={styles.capabilityName}>{capability.name}</h4>
                    {capability.enabled ? (
                      <span className={styles.enabledBadge}>✓ Enabled</span>
                    ) : (
                      <span className={styles.disabledBadge}>Disabled</span>
                    )}
                  </div>
                  <p className={styles.capabilityDesc}>{capability.description}</p>
                  {capability.features && capability.features.length > 0 && (
                    <ul className={styles.featureList}>
                      {capability.features.map((feature, idx) => (
                        <li key={idx} className={styles.featureItem}>
                          <span className={styles.featureCheck}>✓</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  )}
                  {capability.tools && capability.tools.length > 0 && (
                    <div className={styles.toolsSection}>
                      <span className={styles.toolsLabel}>Tools:</span>
                      <div className={styles.toolsList}>
                        {capability.tools.map((tool) => (
                          <span key={tool} className={styles.toolBadge}>{tool}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {activeTab === 'customize' && (
            <div className={styles.customizeTab}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Assistant Name</label>
                <input
                  type="text"
                  className={styles.input}
                  value={customization.customName || ''}
                  onChange={(e) => setCustomization({ ...customization, customName: e.target.value })}
                  placeholder="Enter a name for your assistant"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Description</label>
                <textarea
                  className={styles.textarea}
                  value={customization.customDescription || ''}
                  onChange={(e) => setCustomization({ ...customization, customDescription: e.target.value })}
                  placeholder="Describe your assistant"
                  rows={3}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  System Instructions
                  <span className={styles.labelHint}>(Customize the AI behavior)</span>
                </label>
                <textarea
                  className={styles.textarea}
                  value={customization.customSystemInstructions || ''}
                  onChange={(e) => setCustomization({ ...customization, customSystemInstructions: e.target.value })}
                  placeholder="Enter system instructions"
                  rows={10}
                />
              </div>

              <div className={styles.capabilitiesPreview}>
                <h4 className={styles.previewTitle}>Included Capabilities</h4>
                <div className={styles.previewList}>
                  {template.capabilities.filter(c => c.enabled).map((cap) => (
                    <div key={cap.id} className={styles.previewItem}>
                      <span className={styles.previewIcon}>{cap.icon}</span>
                      <span>{cap.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <button className={styles.cancelButton} onClick={onClose} disabled={cloning}>
            Cancel
          </button>
          <button 
            className={styles.cloneButton} 
            onClick={handleClone}
            disabled={cloning || !customization.customName?.trim()}
          >
            {cloning ? (
              <>
                <span className={styles.spinner}></span>
                Creating Assistant...
              </>
            ) : (
              <>
                🚀 Clone & Create Assistant
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateDetailModal;
